import React, { useState, useImperativeHandle, forwardRef, useEffect } from 'react';
import CBS_HardSkills from './Shards/CBS_HardSkills';
import CBS_AreasOfExpertise from './Shards/CBS_AreasOfExpertise';
import CBS_SoftSkills from './Shards/CBS_SoftSkills';
// import IL_ExplicitRequirements from './Shards/IL_ExplicitRequirements';
import IL_UserQuestions from './Shards/IL_UserQuestions';
import IL_CandidateResponsibilities from './Shards/IL_CandidateResponsibilities';
import IL_Oddities from './Shards/IL_Oddities';
import IL_OtherItems from './Shards/IL_OtherItems';
import IL_MissingInformation from './Shards/IL_MissingInformation';

const Step_ConfirmCriteria = forwardRef(({ data, onDataChange, evaluationData, onClose, onGenerateEvaluation }, ref) => {
   
  // Log the evaluation data received
  console.log('Step_ConfirmCriteria - evaluationData:', evaluationData);
  console.log('Step_ConfirmCriteria - questionnaires:', evaluationData?.questionnaires);
  console.log('Step_ConfirmCriteria - challenges:', evaluationData?.challenges);

  // State for active tab
  const [activeTab, setActiveTab] = useState('questionnaire');

  // State for target question counts and times - initialize from evaluationData
  const [targetQuestionnaireQuestions, setTargetQuestionnaireQuestions] = useState(
    parseInt(evaluationData?.questionnaires?.[0]?.target_questionnaire_questions) || 10
  );
  const [targetChallengeQuestions, setTargetChallengeQuestions] = useState(
    parseInt(evaluationData?.challenges?.[0]?.target_challenge_questions) || 10
  );
  const [targetQuestionnaireTime, setTargetQuestionnaireTime] = useState(
    parseInt(evaluationData?.questionnaires?.[0]?.target_questionnaire_time) || 30
  );
  const [targetChallengeTime, setTargetChallengeTime] = useState(
    parseInt(evaluationData?.challenges?.[0]?.target_challenge_time) || 60
  );
  
  // State for challenge breadth and type - initialize from evaluationData
  const [challengeBreadth, setChallengeBreadth] = useState(
    evaluationData?.challenges?.[0]?.breadth || 'Mixed'
  );
  const [challengeType, setChallengeType] = useState(
    evaluationData?.challenges?.[0]?.challenge_type || 'quiz'
  );

  // State for questionnaire criteria - our working copy that we edit
  const [questionnaireCriteria, setQuestionnaireCriteria] = useState(
    evaluationData?.questionnaires?.[0]?.questionnaire_criteria_json
  );

  // State for challenge criteria - our working copy that we edit
  const [challengeCriteria, setChallengeCriteria] = useState(
    evaluationData?.challenges?.[0]?.challenge_criteria_json
  );

  // Check if questionnaire should be skipped
  const skipQuestionnaire = evaluationData?.step_questionnaire === false;

  // Update activeTab if skipQuestionnaire changes
  useEffect(() => {
    if (skipQuestionnaire) {
      setActiveTab('challenge');
    }
  }, [skipQuestionnaire]);

  // Get evaluation type from evaluationData
  const evaluationType = evaluationData?.type || 'VSSkillset';

  // Get component labels based on evaluation type and context (questionnaire/challenge)
  const getComponentLabel = (component, context = 'questionnaire') => {
    const labelMap = {
      questionnaire: {
        hardSkills: {
          VSJob: 'Hard Skills (Required & Nice To Have)',
          VSResume: 'Hard Skills (Top 5)',
          VSSkillset: 'Hard Skills'
        },
        softSkills: {
          VSJob: 'Soft Skills (Required & Nice To Have)',
          VSResume: 'Soft Skills',
          VSSkillset: 'Soft Skills'
        },
        areasOfExpertise: {
          VSJob: 'Areas of Expertise (Required & Nice To Have)',
          VSResume: 'Areas of Expertise (Top 3)',
          VSSkillset: 'Areas of Expertise'
        },
        explicitRequirements: {
          VSJob: 'Job Requirements',
          VSResume: '-',
          VSSkillset: 'Explicit Requirements'
        },
        candidateResponsibilities: {
          VSJob: 'Job Responsibilities',
          VSResume: '-',
          VSSkillset: 'Talent Responsibilities'
        },
        userQuestions: {
          VSJob: 'Explicit Questions',
          VSResume: 'Explicit Questions',
          VSSkillset: 'Explicit Questions'
        },
        oddities: {
          VSJob: '-',
          VSResume: 'Resume Oddities',
          VSSkillset: '-'
        },
        otherItems: {
          VSJob: '-',
          VSResume: '-',
          VSSkillset: 'Other Items'
        },
        missingInformation: {
          VSJob: '-',
          VSResume: 'Missing Resume Information',
          VSSkillset: '-'
        }
      },
      challenge: {
        hardSkills: {
          VSJob: 'Hard Skills (Required & Nice To Have)',
          VSResume: 'Hard Skills',
          VSSkillset: 'Hard Skills'
        },
        areasOfExpertise: {
          VSJob: 'Areas of Expertise (Required & Nice To Have)',
          VSResume: 'Areas of Expertise',
          VSSkillset: 'Areas of Expertise'
        },
        candidateResponsibilities: {
          VSJob: 'Job Responsibilities',
          VSResume: '-',
          VSSkillset: '-'
        }
      }
    };

    return labelMap[context]?.[component]?.[evaluationType] || component;
  };

  // Get subtitle based on evaluation data
  const getSubtitle = () => {
    if (evaluationData?.jobs?.job_name && evaluationData?.jobs?.company_name) {
      return `${evaluationData.jobs.job_name} at ${evaluationData.jobs.company_name}`;
    } else if (evaluationData?.talent?.fname && evaluationData?.talent?.lname) {
      return `${evaluationData.talent.fname} ${evaluationData.talent.lname}`;
    }
    return 'Evaluation Setup';
  };

  // Get descriptive text based on active tab
  const getDescriptiveText = () => {
    if (skipQuestionnaire || activeTab === 'challenge') {
      return 'What should we test in the challenge?';
    }
    return 'What should we collect information about?';
  };

  // Helper functions to determine which components to show
  const shouldShowForQuestionnaire = (component) => {
    const componentMap = {
      hardSkills: ['VSJob', 'VSResume', 'VSSkillset'],
      softSkills: ['VSJob', 'VSSkillset'],
      areasOfExpertise: ['VSJob', 'VSResume', 'VSSkillset'],
      explicitRequirements: ['VSJob'],
      candidateResponsibilities: ['VSJob'],
      userQuestions: ['VSJob', 'VSResume', 'VSSkillset'],
      oddities: ['VSResume'],
      otherItems: ['VSSkillset'],
      missingInformation: ['VSResume']
    };
    return componentMap[component]?.includes(evaluationType) || false;
  };

  const shouldShowForChallenge = (component) => {
    const componentMap = {
      hardSkills: ['VSJob', 'VSResume', 'VSSkillset'],
      areasOfExpertise: ['VSJob', 'VSResume', 'VSSkillset'],
      candidateResponsibilities: ['VSJob']
    };
    return componentMap[component]?.includes(evaluationType) || false;
  };

  // Helper functions to check if warnings should be shown (over 10 items)
  const shouldShowWarningForQuestionnaire = (component) => {
    if (!questionnaireCriteria) return false;
    
    switch (component) {
      case 'hardSkills':
        return questionnaireCriteria.hardSkills?.items?.length > 10;
      case 'softSkills':
        return questionnaireCriteria.softSkills?.items?.length > 10;
      case 'areasOfExpertise':
        return questionnaireCriteria.areasOfExpertise?.items?.length > 10;
      default:
        return false;
    }
  };

  const shouldShowWarningForChallenge = (component) => {
    if (!challengeCriteria) return false;
    
    switch (component) {
      case 'hardSkills':
        return challengeCriteria.hardSkills?.items?.length > 10;
      case 'areasOfExpertise':
        return challengeCriteria.areasOfExpertise?.items?.length > 10;
      default:
        return false;
    }
  };

  // Function to handle generate evaluation - called from parent
  const handleGenerateEvaluation = () => {
    if (onGenerateEvaluation) {
      onGenerateEvaluation(
        skipQuestionnaire ? null : questionnaireCriteria, 
        challengeCriteria, 
        skipQuestionnaire ? 0 : targetQuestionnaireQuestions, 
        targetChallengeQuestions, 
        skipQuestionnaire ? 0 : targetQuestionnaireTime, 
        targetChallengeTime, 
        challengeBreadth, 
        challengeType
      );
    }
  };

  // Expose the function to parent component via ref
  useImperativeHandle(ref, () => ({
    handleGenerateEvaluation
  }));

  return (
    <div className="grid h-full relative" style={{ gridTemplateColumns: '66.67% 33.33%' }}>
      {/* Left Column - Criteria Display with blue background, scrollable */}
      <div 
        className="overflow-y-auto" 
        style={{ 
          height: 'calc(100% - 88px)', // Account for navigation bar height
          backgroundColor: 'var(--color-brand-blue-light)',
          WebkitOverflowScrolling: 'touch', // Smooth iOS-style scrolling
          scrollBehavior: 'smooth', // Smooth scrolling behavior
          overscrollBehaviorY: 'auto' // Enable elastic bounce scrolling
        }}
      >
        <div className="p-8 space-y-6">
          {/* Header Section */}
          <div className="text-center mb-8">
            <h2 className="text-4xl font-regular text-gray-900 mb-2 mt-4">Confirm Evaluation Criteria</h2>
            <p className="text-md text-gray-600 mb-8">
              {getSubtitle()}
            </p>
            
            {/* Tabs */}
            <div className="flex justify-center mb-6 mt-4">
              <div className="relative">
                {skipQuestionnaire && (
                  <div className="absolute -top-2 left-20 bg-red-500 text-white text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap z-10">
                    Disabled
                  </div>
                )}
                <div className="flex bg-white rounded-xl border border-gray-300 overflow-hidden">
                  <button
                    onClick={() => !skipQuestionnaire && setActiveTab('questionnaire')}
                    disabled={skipQuestionnaire}
                    className={`px-6 py-3 font-medium text-lg transition-colors ${
                      skipQuestionnaire
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : activeTab === 'questionnaire'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Questionnaire Criteria
                  </button>
                  
                  {/* Challenge Tab */}
                  <button
                    onClick={() => setActiveTab('challenge')}
                    className={`px-6 py-3 font-medium text-lg transition-colors ${
                      activeTab === 'challenge'
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Challenge Criteria
                  </button>
                </div>
              </div>
            </div>
            
            <p className="text-2xl text-black-600">
              {getDescriptiveText()}
            </p>
          </div>
          
          {/* Questionnaire Criteria Section */}
          {activeTab === 'questionnaire' && (
            <div className="mb-8">
            
            {/* Hard Skills Selector */}
            {shouldShowForQuestionnaire('hardSkills') && (
              <CBS_HardSkills
                criteria={questionnaireCriteria}
                onCriteriaChange={(updatedCriteria) => {
                  setQuestionnaireCriteria(updatedCriteria);
                }}
                label={getComponentLabel('hardSkills', 'questionnaire')}
                showWarning={shouldShowWarningForQuestionnaire('hardSkills')}
              />
            )}

            {/* Soft Skills Selector */}
            {shouldShowForQuestionnaire('softSkills') && (
              <CBS_SoftSkills
                criteria={questionnaireCriteria}
                onCriteriaChange={(updatedCriteria) => {
                  setQuestionnaireCriteria(updatedCriteria);
                }}
                label={getComponentLabel('softSkills', 'questionnaire')}
                showWarning={shouldShowWarningForQuestionnaire('softSkills')}
              />
            )}

            {/* Areas of Expertise Selector */}
            {shouldShowForQuestionnaire('areasOfExpertise') && (
              <CBS_AreasOfExpertise
                criteria={questionnaireCriteria}
                onCriteriaChange={(updatedCriteria) => {
                  setQuestionnaireCriteria(updatedCriteria);
                }}
                label={getComponentLabel('areasOfExpertise', 'questionnaire')}
                showWarning={shouldShowWarningForQuestionnaire('areasOfExpertise')}
              />
            )}

            {/* Explicit Requirements Input List */}
            {/* {shouldShowForQuestionnaire('explicitRequirements') && (
              <IL_ExplicitRequirements
                questionnaireCriteria={questionnaireCriteria}
                onCriteriaChange={(updatedCriteria) => {
                  setQuestionnaireCriteria(updatedCriteria);
                }}
                label={getComponentLabel('explicitRequirements')}
              />
            )} */}

            {/* Candidate Responsibilities Input List */}
            {shouldShowForQuestionnaire('candidateResponsibilities') && (
              <IL_CandidateResponsibilities
                questionnaireCriteria={questionnaireCriteria}
                onCriteriaChange={(updatedCriteria) => {
                  setQuestionnaireCriteria(updatedCriteria);
                }}
                label={getComponentLabel('candidateResponsibilities', 'questionnaire')}
              />
            )}

            {/* Oddities Input List */}
            {shouldShowForQuestionnaire('oddities') && (
              <IL_Oddities
                questionnaireCriteria={questionnaireCriteria}
                onCriteriaChange={(updatedCriteria) => {
                  setQuestionnaireCriteria(updatedCriteria);
                }}
                label={getComponentLabel('oddities', 'questionnaire')}
              />
            )}

            {/* Other Items Input List */}
            {shouldShowForQuestionnaire('otherItems') && (
              <IL_OtherItems
                questionnaireCriteria={questionnaireCriteria}
                onCriteriaChange={(updatedCriteria) => {
                  setQuestionnaireCriteria(updatedCriteria);
                }}
                label={getComponentLabel('otherItems', 'questionnaire')}
              />
            )}

            {/* Missing Information Input List */}
            {shouldShowForQuestionnaire('missingInformation') && (
              <IL_MissingInformation
                questionnaireCriteria={questionnaireCriteria}
                onCriteriaChange={(updatedCriteria) => {
                  setQuestionnaireCriteria(updatedCriteria);
                }}
                label={getComponentLabel('missingInformation', 'questionnaire')}
              />
            )}

            {/* User Questions Input List */}
            {shouldShowForQuestionnaire('userQuestions') && (
              <IL_UserQuestions
                questionnaireCriteria={questionnaireCriteria}
                onCriteriaChange={(updatedCriteria) => {
                  setQuestionnaireCriteria(updatedCriteria);
                }}
                label={getComponentLabel('userQuestions', 'questionnaire')}
              />
            )}

            {/* Target Questionnaire Questions and Time Fields */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-base font-medium text-gray-700 mb-2">
                  Target Questionnaire Questions
                </label>
                <input
                  type="number"
                  value={targetQuestionnaireQuestions}
                  onChange={(e) => setTargetQuestionnaireQuestions(parseInt(e.target.value) || 15)}
                  className="w-24 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white text-center"
                  min="1"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-base font-medium text-gray-400 mb-2">
                  Target Time (minutes)
                </label>
                <input
                  type="number"
                  value={targetQuestionnaireTime}
                  onChange={(e) => setTargetQuestionnaireTime(parseInt(e.target.value) || 30)}
                  className="w-24 p-2 border border-gray-200 rounded-md text-sm bg-gray-100 text-center text-gray-400 cursor-not-allowed"
                  min="1"
                  max="300"
                  disabled
                />
              </div>
            </div>

              {/* <div className="bg-white p-4 rounded-lg border border-gray-300">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
                  {questionnaireCriteria ? JSON.stringify(questionnaireCriteria, null, 2) : 'No questionnaire criteria available'}
                </pre>
              </div> */}
            </div>
          )}

          {/* Challenge Criteria Section */}
          {activeTab === 'challenge' && (
            <div className="mb-8">
              
              {/* Hard Skills Selector for Challenge */}
              {shouldShowForChallenge('hardSkills') && (
                <CBS_HardSkills
                  criteria={challengeCriteria}
                  onCriteriaChange={(updatedCriteria) => {
                    setChallengeCriteria(updatedCriteria);
                  }}
                  label={getComponentLabel('hardSkills', 'challenge')}
                  showWarning={shouldShowWarningForChallenge('hardSkills')}
                />
              )}

              {/* Areas of Expertise Selector for Challenge */}
              {shouldShowForChallenge('areasOfExpertise') && (
                <CBS_AreasOfExpertise
                  criteria={challengeCriteria}
                  onCriteriaChange={(updatedCriteria) => {
                    setChallengeCriteria(updatedCriteria);
                  }}
                  label={getComponentLabel('areasOfExpertise', 'challenge')}
                  showWarning={shouldShowWarningForChallenge('areasOfExpertise')}
                />
              )}

              {/* Candidate Responsibilities Input List for Challenge */}
              {shouldShowForChallenge('candidateResponsibilities') && (
                <IL_CandidateResponsibilities
                  questionnaireCriteria={challengeCriteria}
                  onCriteriaChange={(updatedCriteria) => {
                    setChallengeCriteria(updatedCriteria);
                  }}
                  label={getComponentLabel('candidateResponsibilities', 'challenge')}
                />
              )}

              {/* Target Challenge Questions and Time Fields */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Target Challenge Questions
                  </label>
                  <input
                    type="number"
                    value={targetChallengeQuestions}
                    onChange={(e) => setTargetChallengeQuestions(parseInt(e.target.value) || 15)}
                    className="w-24 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white text-center"
                    min="1"
                    max="100"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Target Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={targetChallengeTime}
                    onChange={(e) => setTargetChallengeTime(parseInt(e.target.value) || 60)}
                    className="w-24 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white text-center"
                    min="1"
                    max="300"
                  />
                </div>
              </div>

              {/* Breadth and Challenge Mode Dropdown Fields */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Breadth
                  </label>
                  <select
                    value={challengeBreadth}
                    onChange={(e) => setChallengeBreadth(e.target.value)}
                    className="w-40 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                  >
                    <option value="Mixed">Mixed</option>
                    <option value="Focused">Focused</option>
                    <option value="Involved">Involved</option>
                    <option value="Comprehensive">Comprehensive</option>
                  </select>
                </div>
                <div>
                  <label className="block text-base font-medium text-gray-700 mb-2">
                    Challenge Type
                  </label>
                  <select
                    value={challengeType}
                    onChange={(e) => setChallengeType(e.target.value)}
                    className="w-40 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                  >
                    <option value="quiz">Quiz</option>
                    <option value="interview">Interview</option>
                  </select>
                </div>
              </div>

              {/* <div className="bg-white p-4 rounded-lg border border-gray-300">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap overflow-x-auto">
                  {challengeCriteria ? JSON.stringify(challengeCriteria, null, 2) : 'No challenge criteria available'}
                </pre>
              </div> */}
            </div>
          )}
        </div>
      </div>

      {/* Right Column - Explainer content with vertical center alignment */}
      <div className="bg-white flex items-center justify-center p-8">
        <div className="text-center max-w-sm">
          {/* Centered Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <span className="text-white text-lg font-bold">✓</span>
            </div>
          </div>
          
          {/* Main Heading */}
          <h3 className="text-xl font-semibold text-gray-900 mb-6 leading-tight">
            Review & Confirm the Evaluation Criteria.
          </h3>
          
          {/* Subtext */}
          <p className="text-base text-gray-600 leading-relaxed">
            The Evaluation (i.e. Questionnaire and Challenge) will be generated based on the criteria content on the left.
          </p>
        </div>
      </div>
    </div>
  );
});

export default Step_ConfirmCriteria;
